import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Empty, Loading, PageHeader, Panel } from "../components/common";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { DeleteReportButton } from "../components/ReportCalendar";
import { usePerm } from "../context/PermContext";
import { isMobileSettore, settoreLabel } from "../lib/settori";
import { useAuth } from "../context/AuthContext";
import { hasRole, VIGILANZA_ROLES, VIG_SUPERVISOR_ROLE } from "../lib/roles";
import { Link } from "react-router-dom";

const VIG = "__vig";
const toCard = (r) => ({ ...r, vig: true, settore: `Vigilanza ${r.servizio || ""}`.trim(), attivita_svolte: r.segnalazione, attivita_da_svolgere: r.provvedimenti });
const loadVig = () => api("/local/vig-reports/all").then((d) => (d.reports || []).map(toCard)).catch(() => []);

const byDate = (a, b) => (b.data || "").localeCompare(a.data || "") || (b.created_at || "").localeCompare(a.created_at || "");

async function loadReports(settore, settori, vigHead) {
  if (settore === VIG) return loadVig();
  if (!settore && vigHead) {
    const [v, s] = await Promise.all([loadVig(), loadReports("", settori.filter((x) => !VIGILANZA_ROLES.includes(x)), false)]);
    return [...v, ...s.filter((r) => !VIGILANZA_ROLES.includes(r.settore))].sort(byDate);
  }
  const local = settori.filter((s) => !isMobileSettore(s) && (!settore || s === settore));
  const calls = [];
  if (!settore || isMobileSettore(settore)) calls.push(api(`/shift-reports${settore ? `?settore=${encodeURIComponent(settore)}` : ""}`).then((d) => (d.reports || []).filter((r) => settore || settori.includes(r.settore))));
  if (local.length) calls.push(api(`/web/shift-reports${settore ? `?settore=${encodeURIComponent(settore)}` : ""}`).then((d) => d.reports || []));
  const res = await Promise.all(calls.map((p) => p.catch(() => [])));
  return res.flat().sort(byDate);
}

const images = (r) => {
  let list = r.immagini;
  if (typeof list === "string") { try { list = JSON.parse(list); } catch { list = []; } }
  return [...(Array.isArray(list) ? list : []), r.immagine].filter(Boolean);
};

const ReportCard = ({ r, onImage, onDeleted }) => (
  <Panel className="!p-5" data-testid={`all-shift-report-${r.id}`}>
    <div className="flex flex-wrap gap-2 items-center mb-2">
      <span className="font-semibold text-[var(--navy)]">{fmtDate(r.data)}</span>
      {r.turno && <span className="tag tag-teal capitalize">{r.turno}</span>}
      {r.settore && <span className="tag tag-slate">{r.settore}</span>}
      {r.updated_at && <span className="tag tag-amber">Modificato</span>}
      <span className="ml-auto">{r.vig ? <Link to={`/vigilanza/report/${r.id}?m=${(r.data || "").slice(0, 7)}`} className="text-sm font-semibold text-[var(--teal)]" data-testid={`all-shift-vig-detail-${r.id}`}>Dettaglio</Link> : <DeleteReportButton r={r} onDone={onDeleted} />}</span>
    </div>
    <p className="text-sm font-medium text-[var(--navy)] mb-1">{r.author_name}</p>
    <p className="text-sm text-slate-600 whitespace-pre-wrap"><b>Effettuate:</b> {r.attivita_svolte || "—"}</p>
    {r.attivita_da_svolgere && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"><b>Da svolgere:</b> {r.attivita_da_svolgere}</p>}
    {images(r).length > 0 && <div className="flex flex-wrap gap-2 mt-3">{images(r).map((src, i) => <button key={i} onClick={() => onImage(src)} data-testid={`all-shift-report-img-${r.id}-${i}`}><img src={src} alt="Foto report" className="w-16 h-16 rounded-lg object-cover" /></button>)}</div>}
    <p className="text-xs text-slate-400 mt-2">Inviato il {fmtDate(r.created_at, true)}</p>
  </Panel>
);

export default function AllShiftReports() {
  const [settore, setSettore] = useState("");
  const [reports, setReports] = useState(null);
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  const [img, setImg] = useState(null);
  const { settori, admin } = usePerm();
  const { user } = useAuth();
  const vigHead = admin || hasRole(user, [VIG_SUPERVISOR_ROLE]);
  const key = settori.join("|");
  const tabs = [["", "Tutti"], ...settori.filter((s) => !VIGILANZA_ROLES.includes(s)).map((s) => [s, settoreLabel(s)]), ...(vigHead || settori.some((s) => VIGILANZA_ROLES.includes(s)) ? [[VIG, "Vigilanza (Fiduciari e GPG)"]] : [])];
  useEffect(() => {
    let alive = true;
    setReports(null);
    loadReports(settore, key ? key.split("|") : [], vigHead).then((l) => alive && setReports(l));
    return () => { alive = false; };
  }, [settore, key, vigHead]);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (reports || []).filter((r) => (!day || r.data === day) && (!n || [r.author_name, r.attivita_svolte, r.attivita_da_svolgere].some((v) => (v || "").toLowerCase().includes(n))));
  }, [reports, q, day]);
  const [limit, setLimit] = useState(50);
  useEffect(() => { setLimit(50); }, [settore, q, day]);
  return (
    <div>
      <PageHeader subtitle="Report" title="Report di fine turno" />
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <Tabs value={settore} onValueChange={setSettore}><TabsList className="flex-wrap h-auto">{tabs.map(([v, l]) => <TabsTrigger key={l} value={v} data-testid={`all-shift-tab-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}>{l}</TabsTrigger>)}</TabsList></Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o attività" className="pl-9 bg-white" data-testid="all-shift-search" /></div>
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-auto bg-white" data-testid="all-shift-date" />
      </div>
      {!reports ? <Loading /> : list.length === 0 ? <Empty title="Nessun report" text="Modifica i filtri di ricerca." /> : (
        <>
          <p className="text-sm text-slate-500 mb-3" data-testid="all-shift-count">{list.length} report</p>
          <div className="space-y-3" data-testid="all-shift-reports">{list.slice(0, limit).map((r) => <ReportCard key={r.id} r={r} onImage={setImg} onDeleted={() => setReports((l) => l.filter((x) => x.id !== r.id))} />)}</div>
          {list.length > limit && <button onClick={() => setLimit(limit + 50)} className="mt-4 w-full py-3 rounded-full border text-sm font-medium text-[var(--navy)] hover:bg-white" data-testid="all-shift-more">Mostra altri</button>}
        </>
      )}
      <Dialog open={!!img} onOpenChange={(v) => !v && setImg(null)}>
        <DialogContent className="max-w-3xl p-2"><DialogTitle className="sr-only">Foto</DialogTitle>{img && <img src={img} alt="Foto report" className="w-full rounded-lg" />}</DialogContent>
      </Dialog>
    </div>
  );
}
